import { type ReactNode } from 'react';
import type {
  ProjectWithDetails,
  AcceptRefinementResponse,
  ImageRefDTO,
} from '@historyjki/shared';
import {
  IMAGE_MODEL_PRICES_USD,
  normalizeSizePreset,
  resolveGenerationSpec,
} from '@historyjki/shared';
import { formatApproxUsd } from '../../compose/SizePresetPicker';
import { Modal } from '../../ui/Modal';
import { r2Url } from '../../../utils/r2Url';
import { Button } from '../../ui/Button';
import { IntentStep } from './steps/IntentStep';
import { DescribeStep } from './steps/DescribeStep';
import { ReviewStep } from './steps/ReviewStep';
import { ReferencesStep } from './steps/ReferencesStep';
import { GenerateStep } from './steps/GenerateStep';
import { CompareStep } from './steps/CompareStep';
import {
  useRefineImage,
  STEP_ORDER,
  STEP_LABELS,
} from '../../../hooks/useRefineImage';

interface RefineImageModalProps {
  open: boolean;
  project: ProjectWithDetails;
  imageRef: ImageRefDTO;
  currentR2Key: string;
  targetLabel: string;
  onClose: () => void;
  onAccepted: (result: AcceptRefinementResponse) => void;
}

export function RefineImageModal({
  open,
  project,
  imageRef,
  currentR2Key,
  targetLabel,
  onClose,
  onAccepted,
}: RefineImageModalProps) {
  const {
    step,
    session,
    setSession,
    accepting,
    rejecting,
    canProceedIntent,
    canProceedReview,
    handleClose,
    handleIntentNext,
    handleDescribeComplete,
    handleRegenerateDescription,
    handleReviewNext,
    handleReferencesGenerate,
    handleGenerateComplete,
    handleAcceptRefined,
    handleKeepCurrent,
    goBack,
  } = useRefineImage({ open, imageRef, currentR2Key, onClose, onAccepted });

  const currentImageUrl = r2Url(currentR2Key);
  const stepIdx = STEP_ORDER.indexOf(step);

  // Upfront cost: a bank take's refinement runs at the take's footprint
  // preset (the API inherits it), so the button quotes that tier; other
  // targets refine at the legacy standard tier.
  const inheritedPreset =
    imageRef.kind === 'bank_image'
      ? normalizeSizePreset(
          project.bank_images?.find((b) => b.id === imageRef.bank_image_id)
            ?.size_preset,
        )
      : null;
  const refineCostPerImage = inheritedPreset
    ? resolveGenerationSpec(inheritedPreset, project.format_preset).estCostUsd
    : IMAGE_MODEL_PRICES_USD['gemini-2.5-flash-image'];
  const refineCost = formatApproxUsd(refineCostPerImage * session.variantCount);

  // ─── Footer buttons per step ──────────────────────────────────────

  let footer: ReactNode = null;

  if (step === 'intent') {
    footer = (
      <>
        <Button variant="ghost" onClick={handleClose}>Cancel</Button>
        <span className="modal__foot-spacer" />
        <Button onClick={handleIntentNext} disabled={!canProceedIntent}>
          Next &rarr;
        </Button>
      </>
    );
  } else if (step === 'review') {
    footer = (
      <>
        <Button variant="ghost" onClick={goBack}>&larr; Back</Button>
        <Button variant="secondary" onClick={handleRegenerateDescription}>
          Regenerate description
        </Button>
        <span className="modal__foot-spacer" />
        <Button onClick={handleReviewNext} disabled={!canProceedReview}>
          Next &rarr;
        </Button>
      </>
    );
  } else if (step === 'references') {
    footer = (
      <>
        <Button variant="ghost" onClick={goBack}>&larr; Back</Button>
        <span className="modal__foot-spacer" />
        <Button
          onClick={handleReferencesGenerate}
          title={`Estimated cost for ${session.variantCount} ${session.variantCount === 1 ? 'image' : 'images'}`}
        >
          Generate refined image · {refineCost}
        </Button>
      </>
    );
  } else if (step === 'compare') {
    footer = (
      <>
        <Button variant="ghost" onClick={goBack} disabled={accepting || rejecting}>
          &larr; Back
        </Button>
        <span className="modal__foot-spacer" />
        <Button
          variant="secondary"
          onClick={handleKeepCurrent}
          loading={rejecting}
          disabled={accepting}
        >
          Use current
        </Button>
        <Button onClick={handleAcceptRefined} loading={accepting} disabled={rejecting}>
          Use refined
        </Button>
      </>
    );
  }

  const stepper = (
    <div className="refine-stepper" aria-label="Refinement steps">
      {STEP_ORDER.map((s, i) => (
        <div
          key={s}
          className={
            'refine-stepper__step' +
            (i === stepIdx ? ' refine-stepper__step--current' : '') +
            (i < stepIdx ? ' refine-stepper__step--done' : '')
          }
        >
          <span className="refine-stepper__step-num">{i + 1}</span>
          {STEP_LABELS[s]}
        </div>
      ))}
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`Refine ${targetLabel}`}
      size="lg"
      headerExtra={stepper}
      footer={footer}
    >
      {step === 'intent' && (
        <IntentStep
          value={session.intent}
          onChange={(v) => setSession((prev) => ({ ...prev, intent: v }))}
        />
      )}

      {step === 'describing' && (
        <DescribeStep
          imageRef={imageRef}
          intent={session.intent}
          onComplete={handleDescribeComplete}
          onBack={goBack}
        />
      )}

      {/* Compare against null, not truthiness: clearing the editor makes
          editedDescription the empty string, and the step must stay mounted
          so the user can keep typing their own JSON. */}
      {step === 'review' && session.currentDescription != null && session.editedDescription != null && (
        <ReviewStep
          currentDescription={session.currentDescription}
          editedDescription={session.editedDescription}
          onEditedChange={(v) =>
            setSession((prev) => ({ ...prev, editedDescription: v }))
          }
        />
      )}

      {step === 'references' && (
        <ReferencesStep
          project={project}
          selectedIds={session.selectedElementIds}
          onToggle={(id) =>
            setSession((prev) => ({
              ...prev,
              selectedElementIds: prev.selectedElementIds.includes(id)
                ? prev.selectedElementIds.filter((x) => x !== id)
                : [...prev.selectedElementIds, id],
            }))
          }
          variantCount={session.variantCount}
          onVariantCountChange={(count) =>
            setSession((prev) => ({ ...prev, variantCount: count }))
          }
        />
      )}

      {step === 'generating' && session.editedDescription != null && (
        <GenerateStep
          imageRef={imageRef}
          updatedDescription={session.editedDescription}
          selectedElementIds={session.selectedElementIds}
          intent={session.intent}
          variantCount={session.variantCount}
          onComplete={handleGenerateComplete}
          onBack={goBack}
        />
      )}

      {step === 'compare' && session.candidateImageUrl && (
        <CompareStep
          currentImageUrl={currentImageUrl}
          candidateImageUrl={session.candidateImageUrl}
        />
      )}
    </Modal>
  );
}
